import $ from 'cash-dom';
import FiltersMixin from './mixins/FiltersMixin';
import PpcFilter from './PpcFilter';

/**
 * PpcFilteredItems is responsible for managing the filtering and visibility
 * of a given list of vendors. It listens for filter change events and updates
 * the visible vendors in the list based on the current filter criteria. It ensures
 * a maximum number of vendors are displayed as specified.
 *
 * @class
 *
 * @example
 * ```js
 * new PpcFilteredItems({
 *     $vendorsListContainer: $('.vendor-reviews-list'),
 *     defaultVisibleVendorsCount: Infinity,
 *     singleVendorSelector: '.ppc-single-vendor-review',
 * }).init();
 * ```
 *
 * @param {jQuery|null} [$vendorsListContainer=null] - The container element holding the list of vendors. Defaults to `.duplicated-list-holder`.
 * @param {string} [singleVendorSelector='.ppc-single-vendor'] - The selector used to identify individual vendor elements within the list.
 *
 * @mixes FiltersMixin
 */
class PpcFilteredItems {
	constructor({
		$vendorsListContainer,
		defaultVisibleVendorsCount,
		singleVendorSelector = '.ppc-single-vendor',
	}) {
		this._$vendorsListContainer = $vendorsListContainer;
		this._singleVendorSelector = singleVendorSelector;
		this._resetVendorsList();
		this._dontRefillIndexesForEditorChoice = true;
		this._visibleVendorsCount = this._$vendorsListContainer.data('visible-vendors-count') || defaultVisibleVendorsCount;

		Object.assign(this, FiltersMixin);
	}

	init() {
		if (!this._$vendorsList.length) {
			return;
		}

		$(document).on('filters-change', () => {
			this._filterVendors();
			this._refillIndexes();
		});
	}

	_resetVendorsList() {
		this._$vendorsList = this._$vendorsListContainer.find(this._singleVendorSelector).not('.cross-chart-inside-list-vendor');
	}

	_filterVendors() {
		const vendorsToShow = this._getVendorsToShow();

		if (!vendorsToShow.length) {
			return;
		}

		this._reorederVendorsList(vendorsToShow);
		this._hideShowNeededVendors(vendorsToShow);
	}

	_getVendorsToShow() {
		const $mainChartVendorsList = PpcFilter.getInstance().$vendorsList;

		const vendorsToShow = [];

		$mainChartVendorsList.each((i, vendor) => {
			const $vendor = $(vendor);

			if (!$vendor.hasClass('hidden')) {
				vendorsToShow.push($vendor.data('vendor-slug'));
			}
		});

		return vendorsToShow;
	}

	_hideShowNeededVendors(vendorsToShow) {
		let visibleIndex = 0;

		this._$vendorsList.each((i, vendor) => {
			const $vendor = $(vendor);

			if (vendorsToShow.includes($vendor.data('vendor-slug')) && visibleIndex < this._visibleVendorsCount) {
				$vendor.removeClass('hidden');

				if (visibleIndex === 0) {
					this._showFirstVendorFeatures($vendor);
				} else {
					this._hideFirstVendorFeatures($vendor);
				}

				++visibleIndex;
			} else {
				$vendor.addClass('hidden');
			}
		});
	}
}

export default PpcFilteredItems;